import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@forgenetwork/ui';
import {
  Shield,
  FileText,
  CheckCircle2,
  AlertCircle,
  Clock,
  Download,
  RefreshCw,
  ExternalLink,
  Filter,
} from 'lucide-react';

export const Route = createFileRoute('/admin/compliance')({
  component: AdminCompliancePage,
});

type ComplianceFramework = 'SOC2' | 'HIPAA' | 'GDPR' | 'ISO27001' | 'PCI_DSS' | 'NIST';
type ControlStatus = 'implemented' | 'partial' | 'not_implemented' | 'not_applicable';
type AssessmentStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

interface ComplianceControl {
  id: string;
  controlId: string;
  name: string;
  description: string;
  framework: ComplianceFramework;
  category: string;
  status: ControlStatus;
  evidence: string[];
  lastAssessed: string;
  assessedBy: string | null;
  notes: string | null;
}

interface FrameworkSummary {
  framework: ComplianceFramework;
  totalControls: number;
  implemented: number;
  partial: number;
  notImplemented: number;
  notApplicable: number;
  compliancePercentage: number;
  lastAssessment: string;
  nextAssessment: string | null;
}

interface ComplianceReport {
  id: string;
  framework: ComplianceFramework;
  generatedAt: string;
  generatedBy: string;
  status: AssessmentStatus;
  findings: number;
  recommendations: string[];
  downloadUrl: string | null;
}

function AdminCompliancePage(): React.JSX.Element {
  const [selectedFramework, setSelectedFramework] = React.useState<ComplianceFramework | 'all'>('all');
  const [selectedStatus, setSelectedStatus] = React.useState<ControlStatus | 'all'>('all');
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Generate compliance data (in production, this would come from API)
  const complianceData = React.useMemo(() => {
    const now = new Date();

    const frameworks: FrameworkSummary[] = [
      {
        framework: 'SOC2',
        totalControls: 64,
        implemented: 58,
        partial: 4,
        notImplemented: 1,
        notApplicable: 1,
        compliancePercentage: 94,
        lastAssessment: new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        nextAssessment: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        framework: 'GDPR',
        totalControls: 32,
        implemented: 30,
        partial: 1,
        notImplemented: 0,
        notApplicable: 1,
        compliancePercentage: 97,
        lastAssessment: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        nextAssessment: new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        framework: 'ISO27001',
        totalControls: 114,
        implemented: 98,
        partial: 10,
        notImplemented: 4,
        notApplicable: 2,
        compliancePercentage: 89,
        lastAssessment: new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        nextAssessment: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      },
      {
        framework: 'HIPAA',
        totalControls: 45,
        implemented: 40,
        partial: 3,
        notImplemented: 0,
        notApplicable: 2,
        compliancePercentage: 95,
        lastAssessment: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        nextAssessment: new Date(now.getTime() + 70 * 24 * 60 * 60 * 1000).toISOString(),
      },
    ];

    const controls: ComplianceControl[] = [
      {
        id: 'ctrl-1',
        controlId: 'SOC2-CC6.1',
        name: 'Logical Access Controls',
        description: 'The entity implements logical access security software, infrastructure, and architectures.',
        framework: 'SOC2',
        category: 'Security',
        status: 'implemented',
        evidence: ['Access control policy', 'RBAC implementation', 'Audit logs'],
        lastAssessed: new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        assessedBy: 'Security Team',
        notes: 'Full RBAC implementation with admin verification',
      },
      {
        id: 'ctrl-2',
        controlId: 'GDPR-Art.17',
        name: 'Right to Erasure',
        description: 'Data subjects have the right to have personal data erased.',
        framework: 'GDPR',
        category: 'Data Subject Rights',
        status: 'implemented',
        evidence: ['Data deletion API', 'User account deletion feature', 'Retention policy'],
        lastAssessed: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        assessedBy: 'Privacy Officer',
        notes: 'Automated data deletion workflow implemented',
      },
      {
        id: 'ctrl-3',
        controlId: 'ISO27001-A.12.4',
        name: 'Logging and Monitoring',
        description: 'Events shall be logged and evidence preserved.',
        framework: 'ISO27001',
        category: 'Operations Security',
        status: 'implemented',
        evidence: ['Audit log system', 'Log retention policy', 'Monitoring alerts'],
        lastAssessed: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        assessedBy: 'IT Security',
        notes: 'Active audit logging with security event tracking',
      },
      {
        id: 'ctrl-4',
        controlId: 'SOC2-CC7.2',
        name: 'Incident Response',
        description: 'The entity has defined incident response procedures.',
        framework: 'SOC2',
        category: 'Security',
        status: 'partial',
        evidence: ['Incident response plan draft'],
        lastAssessed: new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        assessedBy: 'Security Team',
        notes: 'Procedure documentation in progress',
      },
      {
        id: 'ctrl-5',
        controlId: 'HIPAA-164.312(d)',
        name: 'Person or Entity Authentication',
        description: 'Implement procedures to verify the identity of a person or entity.',
        framework: 'HIPAA',
        category: 'Technical Safeguards',
        status: 'implemented',
        evidence: ['JWT authentication', 'Admin verification', 'Session management'],
        lastAssessed: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        assessedBy: 'Compliance Officer',
        notes: 'Strong authentication with session controls',
      },
      {
        id: 'ctrl-6',
        controlId: 'ISO27001-A.8.1',
        name: 'Asset Inventory',
        description: 'Assets associated with information shall be identified and inventoried.',
        framework: 'ISO27001',
        category: 'Asset Management',
        status: 'partial',
        evidence: ['Partial inventory spreadsheet'],
        lastAssessed: new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000).toISOString(),
        assessedBy: 'IT Team',
        notes: 'Migrating to automated asset discovery',
      },
    ];

    const reports: ComplianceReport[] = [
      {
        id: 'report-1',
        framework: 'SOC2',
        generatedAt: new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000).toISOString(),
        generatedBy: 'External Auditor',
        status: 'completed',
        findings: 2,
        recommendations: ['Enhance incident response procedures', 'Document change management process'],
        downloadUrl: '/reports/soc2-type2-2024.pdf',
      },
      {
        id: 'report-2',
        framework: 'GDPR',
        generatedAt: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        generatedBy: 'Privacy Officer',
        status: 'completed',
        findings: 1,
        recommendations: ['Update privacy policy with new data processing activities'],
        downloadUrl: '/reports/gdpr-assessment-2024.pdf',
      },
      {
        id: 'report-3',
        framework: 'ISO27001',
        generatedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        generatedBy: 'Internal Audit',
        status: 'in_progress',
        findings: 4,
        recommendations: [],
        downloadUrl: null,
      },
    ];

    const totalControls = frameworks.reduce((sum, f) => sum + f.totalControls, 0);
    const totalImplemented = frameworks.reduce((sum, f) => sum + f.implemented, 0);
    const overallScore = Math.round((totalImplemented / totalControls) * 100);

    return {
      overview: {
        overallScore,
        frameworksTracked: frameworks.length,
        controlsImplemented: totalImplemented,
        totalControls,
        pendingAssessments: 1,
        criticalFindings: 0,
      },
      frameworks,
      controls,
      reports,
    };
  }, []);

  // Filter controls based on selections
  const filteredControls = React.useMemo(() => {
    return complianceData.controls.filter((control) => {
      if (selectedFramework !== 'all' && control.framework !== selectedFramework) return false;
      if (selectedStatus !== 'all' && control.status !== selectedStatus) return false;
      return true;
    });
  }, [complianceData.controls, selectedFramework, selectedStatus]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  const getStatusBadge = (status: ControlStatus) => {
    switch (status) {
      case 'implemented':
        return <Badge className="bg-green-500">Implemented</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-500">Partial</Badge>;
      case 'not_implemented':
        return <Badge variant="destructive">Not Implemented</Badge>;
      case 'not_applicable':
        return <Badge variant="secondary">N/A</Badge>;
    }
  };

  const getAssessmentStatusBadge = (status: AssessmentStatus) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-500">Completed</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500">In Progress</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTimeUntil = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 30) return `${diffDays} days`;
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''}`;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Compliance Dashboard</h1>
            <p className="text-muted-foreground mt-2">
              Track compliance frameworks, control implementations, and audit reports
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button variant="outline">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Score</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceData.overview.overallScore}%</div>
              <Progress value={complianceData.overview.overallScore} className="mt-2 h-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Frameworks</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceData.overview.frameworksTracked}</div>
              <p className="text-xs text-muted-foreground">Being tracked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Controls</CardTitle>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {complianceData.overview.controlsImplemented}/{complianceData.overview.totalControls}
              </div>
              <p className="text-xs text-muted-foreground">Implemented</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceData.overview.pendingAssessments}</div>
              <p className="text-xs text-muted-foreground">Assessments</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Critical</CardTitle>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceData.overview.criticalFindings}</div>
              <p className="text-xs text-muted-foreground">Findings</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reports</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceData.reports.length}</div>
              <p className="text-xs text-muted-foreground">Generated</p>
            </CardContent>
          </Card>
        </div>

        {/* Framework Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {complianceData.frameworks.map((framework) => (
            <Card key={framework.framework}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{framework.framework}</CardTitle>
                  <Badge
                    className={
                      framework.compliancePercentage >= 95
                        ? 'bg-green-500'
                        : framework.compliancePercentage >= 80
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }
                  >
                    {framework.compliancePercentage}%
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Progress value={framework.compliancePercentage} className="h-2" />
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Implemented:</span>{' '}
                    <span className="font-medium">{framework.implemented}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Partial:</span>{' '}
                    <span className="font-medium">{framework.partial}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Missing:</span>{' '}
                    <span className="font-medium">{framework.notImplemented}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">N/A:</span>{' '}
                    <span className="font-medium">{framework.notApplicable}</span>
                  </div>
                </div>
                <div className="pt-2 border-t text-xs text-muted-foreground">
                  <div>Last: {formatDate(framework.lastAssessment)}</div>
                  {framework.nextAssessment && (
                    <div>Next: {formatTimeUntil(framework.nextAssessment)}</div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Controls Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Compliance Controls</CardTitle>
                <CardDescription>Detailed view of all compliance controls</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select
                  value={selectedFramework}
                  onValueChange={(v) => setSelectedFramework(v as ComplianceFramework | 'all')}
                >
                  <SelectTrigger className="w-32">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Framework" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="SOC2">SOC2</SelectItem>
                    <SelectItem value="GDPR">GDPR</SelectItem>
                    <SelectItem value="ISO27001">ISO27001</SelectItem>
                    <SelectItem value="HIPAA">HIPAA</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={selectedStatus}
                  onValueChange={(v) => setSelectedStatus(v as ControlStatus | 'all')}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="implemented">Implemented</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="not_implemented">Not Implemented</SelectItem>
                    <SelectItem value="not_applicable">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Control ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Framework</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Assessed</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredControls.map((control) => (
                  <TableRow key={control.id}>
                    <TableCell className="font-mono text-sm">{control.controlId}</TableCell>
                    <TableCell className="font-medium">{control.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{control.framework}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{control.category}</TableCell>
                    <TableCell>{getStatusBadge(control.status)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(control.lastAssessed)}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Reports */}
        <Card>
          <CardHeader>
            <CardTitle>Assessment Reports</CardTitle>
            <CardDescription>Historical compliance assessment reports</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Framework</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>By</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Findings</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {complianceData.reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <Badge variant="outline">{report.framework}</Badge>
                    </TableCell>
                    <TableCell>{formatDate(report.generatedAt)}</TableCell>
                    <TableCell className="text-muted-foreground">{report.generatedBy}</TableCell>
                    <TableCell>{getAssessmentStatusBadge(report.status)}</TableCell>
                    <TableCell>
                      {report.findings > 0 ? (
                        <span className="text-orange-500">{report.findings} finding(s)</span>
                      ) : (
                        <span className="text-green-500">None</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {report.downloadUrl && (
                        <Button variant="ghost" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
